/**
 * Recuperar la contraseña: pedir el enlace, y elegir una contraseña con él.
 *
 * Son las dos únicas pantallas públicas del flujo, y existen sobre todo para los
 * coguías externos: no entran por U-Pasaporte y nunca recibieron una contraseña, así
 * que esta es la puerta por la que estrenan su cuenta.
 */
import { useEffect, useState } from "react";
import { Alert, Button, CircularProgress, Container, Stack, TextField, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/axios";
import { TarjetaAcceso } from "../components/public/TarjetaAcceso";
import { mensajeDeError } from "../utils/errores";

/** La misma tarjeta del login, dentro del layout público que pone barra y pie. */
function Marco({ titulo, bajada, children }: { titulo: string; bajada?: string; children: React.ReactNode }) {
  return (
    <Container maxWidth="sm" sx={{ py: { xs: 5, sm: 8 } }}>
      <TarjetaAcceso titulo={titulo} bajada={bajada}>
        {children}
      </TarjetaAcceso>
    </Container>
  );
}

/** Paso 1: pedir el enlace. */
export function RecuperarPassword() {
  const [email, setEmail] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const navigate = useNavigate();

  const pedir = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post("password/recuperar/", { email });
    } finally {
      // Da lo mismo cómo haya ido: la pantalla dice lo mismo que el backend, que es
      // lo mismo exista o no el correo. Contar más sería delatar quién tiene cuenta.
      setEnviando(false);
      setListo(true);
    }
  };

  if (listo) {
    return (
      <Marco titulo="Revisa tu correo">
        <Alert severity="success" sx={{ mb: 2 }}>
          Si ese correo tiene una cuenta con contraseña, le llegará un enlace para elegir una nueva.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          El enlace sirve por 2 horas y una sola vez. Si entras al sistema con U-Pasaporte, no necesitas contraseña: usa ese botón en
          el login.
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/login")}>
          Volver al inicio de sesión
        </Button>
      </Marco>
    );
  }

  return (
    <Marco titulo="Recuperar contraseña" bajada="Escribe el correo con el que te registraron y te mandamos un enlace para elegir tu contraseña.">
      <form onSubmit={pedir}>
        <TextField
          fullWidth
          required
          type="email"
          label="Correo"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={enviando}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
          <Button type="submit" variant="contained" disabled={enviando || !email}>
            {enviando ? <CircularProgress size={20} /> : "Mandar enlace"}
          </Button>
          <Button variant="text" onClick={() => navigate("/login")} disabled={enviando}>
            Cancelar
          </Button>
        </Stack>
      </form>
    </Marco>
  );
}

/** Paso 2: elegir la contraseña con el token del enlace. */
export function NuevaPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();

  const [valido, setValido] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [repetida, setRepetida] = useState("");
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [listo, setListo] = useState(false);

  // Se pregunta antes de mostrar el formulario: hacerle escribir una contraseña para
  // recién ahí decirle que el enlace venció es hacerle perder el tiempo dos veces.
  useEffect(() => {
    let vivo = true;
    api
      .get("password/nueva/", { params: { token } })
      .then((r) => vivo && setValido(!!r.data.valido))
      .catch(() => vivo && setValido(false));
    return () => {
      vivo = false;
    };
  }, [token]);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== repetida) {
      setError("Las dos contraseñas no son iguales.");
      return;
    }
    setError("");
    setGuardando(true);
    try {
      await api.post("password/nueva/", { token, password });
      setListo(true);
    } catch (err) {
      setError(mensajeDeError(err, "No se pudo cambiar la contraseña."));
    } finally {
      setGuardando(false);
    }
  };

  if (valido === null) {
    return (
      <Marco titulo="Elegir contraseña">
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={26} />
        </Stack>
      </Marco>
    );
  }

  if (listo) {
    return (
      <Marco titulo="Contraseña lista">
        <Alert severity="success" sx={{ mb: 2 }}>
          Ya puedes entrar con tu contraseña nueva.
        </Alert>
        <Button variant="contained" onClick={() => navigate("/login")}>
          Ir a iniciar sesión
        </Button>
      </Marco>
    );
  }

  if (!valido) {
    return (
      <Marco titulo="Este enlace ya no sirve" bajada="Los enlaces duran 2 horas y se pueden usar una sola vez. Pide uno nuevo y listo.">
        <Button variant="contained" onClick={() => navigate("/password/recuperar")}>
          Pedir otro enlace
        </Button>
      </Marco>
    );
  }

  return (
    <Marco titulo="Elige tu contraseña">
      <form onSubmit={guardar}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            fullWidth
            required
            type="password"
            label="Contraseña nueva"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={guardando}
            autoFocus
          />
          <TextField
            fullWidth
            required
            type="password"
            label="Repítela"
            value={repetida}
            onChange={(e) => setRepetida(e.target.value)}
            disabled={guardando}
          />
          <Button type="submit" variant="contained" disabled={guardando || !password || !repetida}>
            {guardando ? <CircularProgress size={20} /> : "Guardar contraseña"}
          </Button>
        </Stack>
      </form>
    </Marco>
  );
}
